import React, { useEffect, useRef, useState } from 'react';
import { SHADCN } from '../theme';

const THRESHOLD = 70;
const MAX_PULL = 140;

// Pull-to-refresh natif pour la PWA. Actif uniquement quand window.scrollY === 0,
// sur appareils tactiles. Tire vers le bas pour recharger la page.
export default function PullToRefresh({ onRefresh }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const active = useRef(false);

  useEffect(() => {
    const isTouch = 'ontouchstart' in window;
    if (!isTouch) return;

    const onStart = (e) => {
      if (window.scrollY > 0 || refreshing) { startY.current = null; return; }
      startY.current = e.touches[0].clientY;
      active.current = true;
    };
    const onMove = (e) => {
      if (!active.current || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) { setPull(0); return; }
      if (window.scrollY > 0) { active.current = false; setPull(0); return; }
      // Résistance : progression non linéaire au-delà du seuil
      const eased = dy < THRESHOLD ? dy : THRESHOLD + (dy - THRESHOLD) * 0.4;
      setPull(Math.min(eased, MAX_PULL));
    };
    const onEnd = () => {
      if (!active.current) return;
      active.current = false;
      startY.current = null;
      if (pull >= THRESHOLD) {
        setRefreshing(true);
        setPull(THRESHOLD);
        if (onRefresh) {
          try { onRefresh(); } catch (_) { window.location.reload(); }
        } else {
          window.location.reload();
        }
      } else {
        setPull(0);
      }
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [pull, refreshing, onRefresh]);

  const progress = Math.min(pull / THRESHOLD, 1);
  const rotation = progress * 360 + (refreshing ? 0 : 0);
  const visible = pull > 0 || refreshing;

  return (
    <div
      aria-hidden={!visible}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          marginTop: Math.max(pull - 36, -36),
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: SHADCN.cardSolid,
          border: `1px solid ${SHADCN.border}`,
          boxShadow: SHADCN.shadowMd,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: visible ? 1 : 0,
          transition: active.current ? 'none' : 'margin-top 200ms ease, opacity 200ms ease',
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: refreshing ? 'none' : 'transform 80ms linear',
            animation: refreshing ? 'ptr-spin 0.8s linear infinite' : 'none',
            color: SHADCN.fg,
          }}
        >
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            d="M4 12a8 8 0 1 1 2.34 5.66"
          />
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 19v-5h5"
          />
        </svg>
      </div>
      <style>{`@keyframes ptr-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
